import {
  Box,
  Card,
  CardContent,
  CardHeader,
  makeStyles,
} from "@material-ui/core";
import { observer } from "mobx-react-lite";
import React from "react";
import { IDesign } from "../interfaces";
import { store } from "../store";
import { formatDateString } from "../utils/strings";
import { toFixedInches } from "../utils/toFixedInches";
import { hornType } from "./InputParameters";
import Model from "./Model";

interface Props {
  design: IDesign;
}

const fields = [
  { key: "driverName", title: "Driver Name" },
  { key: "horizontalBeamWidth", title: "Horizontal Beam Width" },
  { key: "horizontalMaxSize", title: "Horizontal Max Size" },
  { key: "upperFrequency", title: "Upper Frequency" },
  { key: "verticalBeamWidth", title: "Vertical Beam Width" },
  { key: "verticalMaxSize", title: "Vertical Max Size" },
];

const DesignCard = ({ design }: Props) => {
  const { isImperial, selectDesignsToCompare, selectedDesigns } = store;
  const { created, result, parameters, _id } = design;
  const { geometry } = parameters;
  const classes = styles();

  const isSelected = selectedDesigns.find((d) => d.created === created);

  const output = typeof result === "string" ? JSON.parse(result) : result;

  return (
    <Card
      onClick={() => selectDesignsToCompare(design)}
      key={created + _id}
      className={`${classes.card} ${isSelected && classes.isSelected}`}
    >
      <CardHeader subheader={formatDateString(created)} />
      <Box>
        <Box
          minWidth={200}
          display="flex"
          flex={1}
          justifyContent="center"
          ml={1}
        >
          <Model small output={output} geometry={geometry} />
        </Box>
        <Box>
          <CardContent className={classes.cardContent}>
            <Box
              fontSize={10}
              className={classes.text}
              display="flex"
              flexDirection="column"
            >
              <strong style={{ opacity: 0.8 }}>Horn type</strong>
              <label>
                {design.parameters.allowDiffractionSlot
                  ? hornType[1].label
                  : design.parameters.prioritizeLoading
                  ? hornType[2].label
                  : hornType[0].label}
              </label>
            </Box>
            {fields.map(({ key, title }, index) => {
              const isSize = title.includes("Max Size");
              const units = isSize ? (isImperial ? "(in)" : "(mm)") : "";

              const isImperialSize = isSize && isImperial;
              const value = isSize
                ? design.parameters[key].toFixed(2)
                : String(design.parameters[key]);

              return (
                <Box
                  key={`${title}-${index}`}
                  fontSize={10}
                  className={classes.text}
                  display="flex"
                  flexDirection="column"
                >
                  <strong style={{ opacity: 0.8 }}>
                    {`${title + units}: `}
                  </strong>
                  <label>
                    {isImperialSize
                      ? toFixedInches(design.parameters[key])
                      : value}
                  </label>
                </Box>
              );
            })}
          </CardContent>
        </Box>
      </Box>
    </Card>
  );
};

const styles = makeStyles((theme) => ({
  card: {
    margin: "1em",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
    },
  },
  cardContent: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },
  text: { textTransform: "uppercase" },
  isSelected: {
    borderColor: "red",
    borderWidth: 1,
    borderStyle: "solid",
  },
}));

export default observer(DesignCard);
